import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  ArrowLeft,
  AlertTriangle,
  Building2,
  CalendarDays,
  Download,
  FileText,
  Landmark,
  UserRound,
  WalletCards,
} from "lucide-react";

import {
  api,
  downloadFile,
  errMsg,
} from "@/lib/api";

import {
  Card,
  Btn,
  Empty,
} from "@/components/ui-kit";

import {
  StatusBadge,
} from "@/components/StatusBadge";

import {
  toast,
} from "sonner";


const TABS = [
  "Overview",
  "Employment History",
  "Payroll History",
  "Attendance",
  "Payslips",
  "Documents",
];


const money = (value) =>
  Number(value || 0).toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );


const textValue = (value) =>
  value !== undefined &&
  value !== null &&
  value !== ""
    ? value
    : "—";


function InfoItem({
  label,
  value,
}) {
  return (
    <div className="border-b border-slate-100 py-2.5">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
        {label}
      </div>

      <div className="text-sm text-slate-900 mt-1 break-words">
        {textValue(value)}
      </div>
    </div>
  );
}


function SectionTitle({
  icon: Icon,
  title,
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {Icon && (
        <Icon
          size={17}
          className="text-blue-700"
        />
      )}

      <h3 className="font-semibold text-slate-900">
        {title}
      </h3>
    </div>
  );
}


export default function EmployeeProfile() {
  const {
    eid,
  } = useParams();

  const navigate = useNavigate();

  const [
    data,
    setData,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    tab,
    setTab,
  ] = useState("Overview");


  useEffect(() => {
    setLoading(true);

    api
      .get(`/employees/${eid}/detail`)
      .then((r) => {
        setData(r.data);
      })
      .catch((e) => {
        toast.error(errMsg(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [eid]);


  if (loading) {
    return (
      <Card className="p-6">
        Loading employee profile...
      </Card>
    );
  }


  if (!data?.employee) {
    return (
      <Empty
        title="Employee not found"
        hint="The employee profile could not be loaded."
      />
    );
  }


  const emp = data.employee;

  const current =
    data.current_deployment || {};

  const payroll =
    data.payroll_history || [];

  const attendance =
    data.attendance_history || [];

  const deployments =
    data.deployments || [];

  const documents =
    data.documents || [];

  const latestPayroll =
    payroll[0] || {};


  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-5">
        <div className="flex items-start gap-3">
          <Btn
            variant="outline"
            onClick={() =>
              navigate("/employees")
            }
          >
            <ArrowLeft size={16} />
          </Btn>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-950">
                {emp.name || "Employee"}
              </h1>

              <StatusBadge
                status={
                  emp.active
                    ? "active"
                    : "inactive"
                }
              />
            </div>

            <div className="text-sm text-slate-500 mt-1">
              UAN: {emp.uan || "—"}
              {" · "}
              Employee Code: {
                emp.emp_code ||
                emp.employee_no ||
                "—"
              }
            </div>
          </div>
        </div>
      </div>

      {data?.inactivity_alert && (
        <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle
              size={20}
              className="text-red-600 mt-0.5 shrink-0"
            />

            <div>
              <div className="font-semibold text-red-800">
                Inactive Employee Alert
              </div>

              <div className="text-sm text-red-700 mt-1">
                No positive working-day activity was recorded for{" "}
                <span className="font-semibold">
                  {data.inactivity_alert.month}
                </span>
                {" — "}
                {data.inactivity_alert.reason}.
              </div>

              <div className="text-xs text-red-600 mt-1">
                This is an administrative warning only. The employee remains active until an administrator changes the status.
              </div>
            </div>
          </div>
        </div>
      )}


      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Current Client
          </div>

          <div className="font-semibold mt-1">
            {
              current.client_name ||
              "Not Deployed"
            }
          </div>
        </Card>

        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Designation
          </div>

          <div className="font-semibold mt-1">
            {emp.designation || "—"}
          </div>
        </Card>

        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Daily Rate
          </div>

          <div className="font-semibold mt-1">
            ₹{
              textValue(
                emp.source_daily_rate ||
                emp.daily_rate
              )
            }
          </div>
        </Card>

        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Latest Net Pay
          </div>

          <div className="font-semibold mt-1">
            ₹{money(latestPayroll.net_pay)}
          </div>
        </Card>
      </div>


      <Card className="mb-5 overflow-x-auto">
        <div className="flex min-w-max border-b border-slate-200">
          {TABS.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() =>
                setTab(name)
              }
              className={
                "px-4 py-3 text-sm font-medium border-b-2 " +
                (
                  tab === name
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                )
              }
            >
              {name}
            </button>
          ))}
        </div>
      </Card>


      {tab === "Overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          <Card className="p-5">
            <SectionTitle
              icon={UserRound}
              title="Personal Details"
            />

            <InfoItem
              label="Employee Name"
              value={emp.name}
            />

            <InfoItem
              label="UAN"
              value={emp.uan}
            />

            <InfoItem
              label="Employee Code"
              value={
                emp.emp_code ||
                emp.employee_no
              }
            />

            <InfoItem
              label="Gender"
              value={emp.gender}
            />

            <InfoItem
              label="Date of Birth"
              value={emp.dob}
            />

            <InfoItem
              label="Mobile"
              value={emp.mobile}
            />

            <InfoItem
              label="PAN"
              value={emp.pan}
            />

            <InfoItem
              label="Aadhaar"
              value={
                emp.aadhaar ||
                emp.aadhar
              }
            />

            <InfoItem
              label="Address"
              value={emp.address}
            />
          </Card>


          <Card className="p-5">
            <SectionTitle
              icon={Building2}
              title="Employment Details"
            />

            <InfoItem
              label="Current Client"
              value={
                current.client_name
              }
            />

            <InfoItem
              label="Current Contract"
              value={
                current.contract_name
              }
            />

            <InfoItem
              label="Designation"
              value={emp.designation}
            />

            <InfoItem
              label="Wage Category"
              value={emp.wage_category}
            />

            <InfoItem
              label="Wage Type"
              value={emp.wage_type}
            />

            <InfoItem
              label="Daily Rate"
              value={
                emp.source_daily_rate ||
                emp.daily_rate
              }
            />

            <InfoItem
              label="Date of Joining"
              value={
                emp.joining_date ||
                emp.effective_date
              }
            />

            <InfoItem
              label="Deployment From"
              value={
                current.from_date
              }
            />

            <InfoItem
              label="State"
              value={emp.state}
            />
          </Card>


          <Card className="p-5">
            <SectionTitle
              icon={Landmark}
              title="Bank & Statutory"
            />

            <InfoItem
              label="Bank Name"
              value={emp.bank_name}
            />

            <InfoItem
              label="Bank Account"
              value={emp.bank_account}
            />

            <InfoItem
              label="IFSC"
              value={emp.ifsc}
            />

            <InfoItem
              label="ESI Number"
              value={emp.esi_number}
            />

            <InfoItem
              label="PF Applicable"
              value={
                emp.pf_applicable
                  ? "Yes"
                  : "No"
              }
            />

            <InfoItem
              label="ESI Applicable"
              value={
                emp.esi_applicable
                  ? "Yes"
                  : "No"
              }
            />

            <InfoItem
              label="PT Applicable"
              value={
                emp.pt_applicable
                  ? "Yes"
                  : "No"
              }
            />

            <InfoItem
              label="Record Status"
              value={
                emp.active
                  ? "Active"
                  : "Inactive"
              }
            />
          </Card>
        </div>
      )}


      {tab === "Employment History" && (
        <Card className="overflow-x-auto">
          {deployments.length ? (
            <table className="nk-table min-w-[900px]">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Contract</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Status</th>
                  <th>Daily Rate Override</th>
                </tr>
              </thead>

              <tbody>
                {deployments.map((d) => (
                  <tr key={d.id}>
                    <td className="font-medium">
                      {d.client_name || "—"}
                    </td>

                    <td>
                      {d.contract_name || "—"}
                    </td>

                    <td>
                      {d.from_date || "—"}
                    </td>

                    <td>
                      {d.to_date || "Present"}
                    </td>

                    <td>
                      <StatusBadge
                        status={
                          d.active
                            ? "active"
                            : "inactive"
                        }
                      />
                    </td>

                    <td>
                      {
                        d.payroll_overrides
                          ?.daily_rate ||
                        "—"
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty
              title="No deployment history"
              hint="No deployment records are available for this employee."
            />
          )}
        </Card>
      )}


      {tab === "Payroll History" && (
        <Card className="overflow-x-auto">
          {payroll.length ? (
            <table className="nk-table min-w-[1250px]">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Client</th>
                  <th>Contract</th>
                  <th>Days</th>
                  <th>Rate</th>
                  <th>Earned Wage</th>
                  <th>PF</th>
                  <th>ESI</th>
                  <th>PT</th>
                  <th>Deductions</th>
                  <th>Net Pay</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {payroll.map((p) => (
                  <tr
                    key={
                      p.entry_id
                    }
                  >
                    <td className="font-semibold">
                      {p.month || "—"}
                    </td>

                    <td>
                      {p.client_name || "—"}
                    </td>

                    <td>
                      {p.contract_name || "—"}
                    </td>

                    <td>
                      {p.working_days || "0"}
                    </td>

                    <td>
                      ₹{money(p.wage_rate)}
                    </td>

                    <td>
                      ₹{money(p.earned_wage)}
                    </td>

                    <td>
                      ₹{money(p.employee_pf)}
                    </td>

                    <td>
                      ₹{money(p.employee_esi)}
                    </td>

                    <td>
                      ₹{money(p.pt)}
                    </td>

                    <td>
                      ₹{money(p.total_deductions)}
                    </td>

                    <td className="font-semibold">
                      ₹{money(p.net_pay)}
                    </td>

                    <td>
                      <StatusBadge
                        status={
                          p.status ||
                          "calculated"
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty
              title="No payroll history"
              hint="Payroll has not yet been calculated for this employee."
            />
          )}
        </Card>
      )}


      {tab === "Attendance" && (
        <Card className="overflow-x-auto">
          {attendance.length ? (
            <table className="nk-table min-w-[900px]">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Client</th>
                  <th>Contract</th>
                  <th>Attendance Inputs</th>
                  <th>Updated</th>
                </tr>
              </thead>

              <tbody>
                {attendance.map(
                  (a, index) => (
                    <tr
                      key={
                        `${a.run_id}-${index}`
                      }
                    >
                      <td className="font-semibold">
                        {a.month || "—"}
                      </td>

                      <td>
                        {a.client_name || "—"}
                      </td>

                      <td>
                        {a.contract_name || "—"}
                      </td>

                      <td>
                        <div className="flex flex-wrap gap-2">
                          {
                            Object.entries(
                              a.inputs || {}
                            ).map(
                              ([key, value]) => (
                                <span
                                  key={key}
                                  className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs"
                                >
                                  {key}: {value}
                                </span>
                              )
                            )
                          }
                        </div>
                      </td>

                      <td>
                        {
                          a.updated_at
                            ? String(
                                a.updated_at
                              ).slice(
                                0,
                                19
                              )
                            : "—"
                        }
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          ) : (
            <Empty
              title="No attendance history"
              hint="No saved attendance inputs are available for this employee."
            />
          )}
        </Card>
      )}


      {tab === "Payslips" && (
        <Card className="overflow-x-auto">
          {payroll.length ? (
            <table className="nk-table min-w-[800px]">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Client</th>
                  <th>Earned Wage</th>
                  <th>Net Pay</th>
                  <th>Payslip</th>
                </tr>
              </thead>

              <tbody>
                {payroll.map((p) => (
                  <tr key={p.entry_id}>
                    <td className="font-semibold">
                      {p.month || "—"}
                    </td>

                    <td>
                      {p.client_name || "—"}
                    </td>

                    <td>
                      ₹{money(p.earned_wage)}
                    </td>

                    <td className="font-semibold">
                      ₹{money(p.net_pay)}
                    </td>

                    <td>
                      <Btn
                        variant="outline"
                        onClick={() =>
                          downloadFile(
                            p.payslip_url,
                            `payslip_${emp.uan || "employee"}_${p.month}.pdf`
                          )
                        }
                      >
                        <Download size={14} />
                        PDF
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty
              title="No payslips"
              hint="Payslips will appear after payroll is calculated."
            />
          )}
        </Card>
      )}


      {tab === "Documents" && (
        <Card className="p-5">
          <SectionTitle
            icon={FileText}
            title="Employee Documents"
          />

          {documents.length ? (
            <div className="space-y-2">
              {documents.map(
                (doc, index) => (
                  <div
                    key={
                      doc.id ||
                      index
                    }
                    className="flex items-center justify-between border border-slate-200 rounded-md p-3"
                  >
                    <div>
                      <div className="font-medium">
                        {
                          doc.name ||
                          doc.document_type ||
                          `Document ${index + 1}`
                        }
                      </div>

                      <div className="text-xs text-slate-500">
                        {
                          doc.number ||
                          doc.file_name ||
                          ""
                        }
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="border border-dashed border-slate-300 rounded-lg p-6 text-center">
              <FileText
                size={28}
                className="mx-auto text-slate-400"
              />

              <div className="font-medium mt-2">
                No employee documents uploaded
              </div>

              <div className="text-sm text-slate-500 mt-1">
                Document upload and Aadhaar/PAN file management can be enabled as a separate module.
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
